// TelcoTrack — unified materials panel (polygon / user / global scopes).
// Detail + category-tree Souhrn, active-only filter, admin edit. Smazat smí jen autor řádku (`user_id`).

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router, type Params } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { ObjectService } from '../../../core/services/object.service';
import {
  MATERIALS_DISPLAY_MODE_QUERY_KEY,
  mergeListQueryParams,
  parseListDisplayMode,
  setMergeQueryDisplayMode,
} from '../../utils/list-query-params.util';
import type { DataTableColumn } from '../ui/data-table/data-table.types';
import { joinMultiFilterCsv, mergeFacetFilterValues, toFilterMultiOptions } from '../../utils/filter-multi.util';
import {
  hasDateRangeFilter,
  isOpenEndedDateFilter,
  resolveDateRangeFilterIso,
  todayLocalCsDate,
  unboundedDateRangeIso,
} from '../../utils/date-filter.util';
import { getObjectTypeName } from '../../utils/object.utils';
import {
  expandObjectTypeFilterForApi,
  filterRowsByObjectTypeFilter,
} from '../../utils/vykop-variant-filter.util';
import { isSafeAppReturnUrl } from '../../utils/app-return-url.util';
import { shouldSendActivePolygonOnly } from '../../utils/active-polygon-filter.util';
import {
  buildMaterialsSummaryTableRows,
  type MaterialsSummaryTableRow,
} from '../../utils/materials-summary-table.util';
import {
  categoryTreeCategoryRowClass,
  categoryTreeIndentClass,
  categoryTreeLeafRowClass,
  categoryTreeTotalRowClass,
} from '../../utils/category-tree-table-styles.util';
import { fetchAllMaterialsPages, type MaterialsFetchParams } from '../../utils/materials-fetch.util';
import {
  fetchMaterialsPlanBySap,
  resolveMaterialsPlanPolygonId,
  type MaterialsPlanBySap,
} from '../../utils/materials-plan-fetch.util';
import {
  MATERIALS_SUMMARY_ACTUAL_CELL_START,
  MATERIALS_SUMMARY_ACTUAL_HDR_START,
  MATERIALS_SUMMARY_PLAN_CELL_START,
  MATERIALS_SUMMARY_PLAN_HDR_START,
  MATERIALS_SUMMARY_VARIANCE_CELL,
  MATERIALS_SUMMARY_VARIANCE_HDR,
} from '../../utils/materials-summary-finance-styles.util';
import {
  buildMaterialsExportAggregated,
  formatMaterialDateTime,
  formatMaterialPrice,
  formatMaterialQuantity,
} from '../../utils/materials-display.util';
import type {
  MaterialAggExportRow,
  MaterialDisplayLine,
  MaterialDisplayMode,
  MaterialPanelRow,
} from '../../utils/materials-panel.types';
import type { PortfolioScope } from '../../utils/portfolio-scope.types';
import { clampPage } from '../../utils/pagination.util';

const FILTER_DEBOUNCE_MS = 200;

@Component({
  selector: 'app-materials-panel',
  templateUrl: './materials-panel.component.html',
})
export class MaterialsPanelComponent implements OnInit, OnChanges, OnDestroy {
  /** Scope: polygon tab, user profile tab, or global portfolio (client fetch-all). */
  @Input() scope: PortfolioScope = 'polygon';
  @Input() polygonId: string | null = null;
  @Input() userId: string | null = null;
  @Input() canAdminEdit = false;

  /** User scope — filters managed by parent (user-detail). */
  @Input() filterPolygonIds: string[] = [];
  @Input() showActiveOnly = true;
  @Input() filterObjectTypes: string[] = [];
  @Input() filterObjectPhases: string[] = [];
  @Input() polygonFilterOptions: { value: string; label: string }[] = [];
  @Input() objectTypeFilterOptions: { value: string; label: string }[] = [];
  @Input() phaseFilterOptions: { value: string; label: string }[] = [];
  @Input() objectLinkReturnUrl: string | null = null;

  /** Polygon scope — optional user filter labels from parent. */
  @Input() showClearFiltersButton = true;

  @Output() filterPolygonIdsChange = new EventEmitter<string[]>();
  @Output() filterObjectTypesChange = new EventEmitter<string[]>();
  @Output() filterObjectPhasesChange = new EventEmitter<string[]>();

  private readonly destroy$ = new Subject<void>();
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  filterUserIds: string[] = [];
  dateFrom: string | null = null;
  dateTo: string | null = todayLocalCsDate();
  rangeApplied = false;
  searchQuery = '';
  displayMode: MaterialDisplayMode = 'detail';
  expandedSummaryCategoryIds = new Set<string>();

  detailRows: MaterialPanelRow[] = [];
  userFilterOptionValues: string[] = [];
  hasMaterialPlan = false;
  materialPlanBySap: MaterialsPlanBySap = new Map();
  isLoading = false;
  errorMessage: string | null = null;

  currentPage = 1;
  readonly itemsPerPage = 100;
  showAllRows = false;

  editingRow: MaterialPanelRow | null = null;
  editQtyStr = '';
  editSaving = false;
  editError: string | null = null;

  constructor(
    private readonly objectService: ObjectService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.displayMode = parseListDisplayMode(
      this.route.snapshot.queryParamMap,
      MATERIALS_DISPLAY_MODE_QUERY_KEY,
      'detail',
    );
    this.loadRows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['polygonId'] && !changes['polygonId'].firstChange && this.scope === 'polygon') {
      this.userFilterOptionValues = [];
      this.loadRows();
    }
    if (changes['userId'] && !changes['userId'].firstChange && this.scope === 'user') {
      this.loadRows();
    }
    if (
      this.scope === 'user' &&
      ((changes['filterPolygonIds'] && !changes['filterPolygonIds'].firstChange) ||
        (changes['filterObjectTypes'] && !changes['filterObjectTypes'].firstChange) ||
        (changes['filterObjectPhases'] && !changes['filterObjectPhases'].firstChange) ||
        (changes['showActiveOnly'] && !changes['showActiveOnly'].firstChange))
    ) {
      this.scheduleReload();
    }
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isPolygonScope(): boolean {
    return this.scope === 'polygon';
  }

  get isUserScope(): boolean {
    return this.scope === 'user';
  }

  get showUserFilter(): boolean {
    return this.isPolygonScope && this.userFilterOptions.length > 0;
  }

  get showPolygonFilter(): boolean {
    return this.isUserScope && this.polygonFilterOptions.length > 0;
  }

  get showObjectTypeFilter(): boolean {
    return this.isUserScope && this.objectTypeFilterOptions.length > 0;
  }

  get showPhaseFilter(): boolean {
    return this.isUserScope && this.filterObjectTypes.length > 0 && this.phaseFilterOptions.length > 0;
  }

  get showChangedByColumn(): boolean {
    return this.isPolygonScope;
  }

  get hasAnyFilter(): boolean {
    return (
      this.filterUserIds.length > 0 ||
      this.rangeApplied ||
      !!this.searchQuery.trim()
    );
  }

  get filteredDetailRows(): MaterialPanelRow[] {
    const query = this.searchQuery.trim().toLocaleLowerCase('cs-CZ');
    let rows = this.isUserScope
      ? filterRowsByObjectTypeFilter(this.detailRows, this.filterObjectTypes)
      : this.detailRows;
    if (!query) return rows;
    return rows.filter((row) => {
      const haystack = [row.name, row.sapCode, row.changedBy, row.supplier]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('cs-CZ');
      return haystack.includes(query);
    });
  }

  get displayLines(): MaterialDisplayLine[] {
    const rows = this.filteredDetailRows;
    return rows.map((r) => ({
      isAggregate: false,
      sapCode: r.sapCode,
      name: r.name,
      quantity: r.quantity,
      lineTotal: r.lineTotal ?? 0,
      unit: r.unit,
      sourceRows: [r],
    }));
  }

  get summaryTableRows(): MaterialsSummaryTableRow[] {
    return buildMaterialsSummaryTableRows({
      sourceRows: this.filteredDetailRows,
      expandedCategoryIds: this.expandedSummaryCategoryIds,
      planBySap: this.materialPlanBySap,
      includePlan: this.hasMaterialPlan,
    });
  }

  get paginatedDisplayLines(): MaterialDisplayLine[] {
    if (this.showAllRows) return this.displayLines;
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.displayLines.slice(start, start + this.itemsPerPage);
  }

  get lineTotalSum(): number {
    let s = 0;
    for (const line of this.displayMode === 'aggregated' ? this.summaryTableRows : this.displayLines) {
      if ('rowType' in line && line.rowType === 'total') {
        s += Number(line.lineTotal) || 0;
      } else if (!('rowType' in line)) {
        s += Number(line.lineTotal) || 0;
      }
    }
    if (this.displayMode === 'aggregated') {
      const totalRow = this.summaryTableRows.find((r) => r.rowType === 'total');
      return totalRow ? Number(totalRow.lineTotal) || 0 : s;
    }
    return s;
  }

  get materialFooterLabel(): string {
    if (hasDateRangeFilter(this.dateFrom, this.dateTo)) {
      return 'Celková cena (zvolené období)';
    }
    return 'Celková cena';
  }

  get userFilterOptions(): { value: string; label: string }[] {
    return toFilterMultiOptions(this.userFilterOptionValues);
  }

  get materialTableColumns(): DataTableColumn<MaterialDisplayLine>[] {
    const cols: DataTableColumn<MaterialDisplayLine>[] = [];
    if (this.displayMode === 'detail') {
      cols.push({
        id: 'usedAt',
        label: 'Datum',
        cell: 'template',
        cellClass: 'text-sm text-neutral-600 dark:text-neutral-400 whitespace-nowrap',
      });
      if (this.showChangedByColumn) {
        cols.push({
          id: 'changedBy',
          label: 'Od koho',
          cell: 'template',
          cellClass: 'text-sm break-all text-neutral-900 dark:text-neutral-100',
        });
      }
    }
    cols.push(
      {
        id: 'name',
        label: 'Materiál',
        cell: 'template',
        stopRowClick: true,
        cellClass: 'text-sm font-medium text-neutral-900 dark:text-neutral-100',
      },
      {
        id: 'sapCode',
        label: 'SAP',
        cell: 'template',
        cellClass: 'text-sm font-mono text-neutral-600 dark:text-neutral-400',
      },
    );
    if (this.displayMode === 'detail') {
      cols.push({
        id: 'supplier',
        label: 'Dodavatel',
        cell: 'template',
        cellClass: 'text-sm text-neutral-600 dark:text-neutral-400',
      });
    }
    cols.push({
      id: 'quantity',
      label: 'Množství',
      align: 'right',
      cell: 'template',
      cellClass: 'text-sm text-neutral-900 dark:text-neutral-100',
    });
    if (this.displayMode === 'detail') {
      cols.push({
        id: 'price',
        label: 'Cena/ks',
        align: 'right',
        cell: 'template',
        cellClass: 'text-sm text-neutral-600 dark:text-neutral-400',
      });
    }
    cols.push({
      id: 'lineTotal',
      label: 'Celkem',
      align: 'right',
      cell: 'template',
      cellClass: 'text-sm font-medium text-neutral-900 dark:text-neutral-100',
    });
    if (this.displayMode === 'detail' && this.canAdminEdit) {
      cols.push({
        id: 'actions',
        label: '',
        align: 'right',
        cell: 'template',
        stopRowClick: true,
        cellClass: 'text-right whitespace-nowrap',
      });
    }
    return cols;
  }

  get summaryTableColumns(): DataTableColumn<MaterialsSummaryTableRow>[] {
    const cols: DataTableColumn<MaterialsSummaryTableRow>[] = [
      {
        id: 'label',
        label: 'Kategorie / materiál',
        cell: 'template',
        headerClass: 'max-w-[16rem]',
        cellClass:
          'max-w-[16rem] break-words whitespace-normal text-neutral-800 dark:text-neutral-200',
      },
      {
        id: 'sapCode',
        label: 'SAP',
        cell: 'template',
        cellClass: 'text-sm font-mono text-neutral-600 dark:text-neutral-400',
      },
      ...(this.hasMaterialPlan
        ? [
            {
              id: 'plannedQuantity',
              label: 'Množství (plán)',
              align: 'right' as const,
              cell: 'template' as const,
              headerClass: MATERIALS_SUMMARY_PLAN_HDR_START,
              cellClass: `text-sm ${MATERIALS_SUMMARY_PLAN_CELL_START}`,
            },
            {
              id: 'quantity',
              label: 'Množství (skutečnost)',
              align: 'right' as const,
              cell: 'template' as const,
              headerClass: MATERIALS_SUMMARY_ACTUAL_HDR_START,
              cellClass: `text-sm ${MATERIALS_SUMMARY_ACTUAL_CELL_START}`,
            },
            {
              id: 'variance',
              label: 'Rozdíl množství',
              align: 'right' as const,
              cell: 'template' as const,
              headerClass: MATERIALS_SUMMARY_VARIANCE_HDR,
              cellClass: `text-sm ${MATERIALS_SUMMARY_VARIANCE_CELL}`,
            },
          ]
        : [
            {
              id: 'quantity',
              label: 'Množství',
              align: 'right' as const,
              cell: 'template' as const,
              cellClass: 'text-sm text-neutral-900 dark:text-neutral-100',
            },
            {
              id: 'lineTotal',
              label: 'Celkem',
              align: 'right' as const,
              cell: 'template' as const,
              cellClass: 'text-sm font-medium text-neutral-900 dark:text-neutral-100',
            },
          ]),
    ];
    return cols;
  }

  get materialTableSummaryOverride(): string | undefined {
    if (this.showAllRows) {
      const count =
        this.displayMode === 'aggregated' ? this.summaryTableRows.length : this.displayLines.length;
      const label = this.displayMode === 'aggregated' ? 'položek souhrnu' : 'záznamů';
      return `Zobrazeno všech ${count} ${label} (bez stránkování)`;
    }
    return undefined;
  }

  onDateFieldBlur(): void {
    if (hasDateRangeFilter(this.dateFrom, this.dateTo)) {
      this.applyDateRange();
      return;
    }
    this.rangeApplied = false;
    this.scheduleReload();
  }

  applyDateRange(): void {
    if (!hasDateRangeFilter(this.dateFrom, this.dateTo)) return;
    this.rangeApplied = true;
    this.scheduleReload();
  }

  onFilterUserIdsChange(values: string[]): void {
    this.filterUserIds = [...values];
    this.scheduleReload();
  }

  onPolygonFilterChange(values: string[]): void {
    this.filterPolygonIdsChange.emit([...values]);
  }

  onObjectTypeFilterChange(values: string[]): void {
    this.filterObjectTypesChange.emit([...values]);
  }

  onObjectPhaseFilterChange(values: string[]): void {
    this.filterObjectPhasesChange.emit([...values]);
  }

  onSearchQueryChange(): void {
    this.resetTablePagination();
  }

  scheduleReload(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.loadRows(), FILTER_DEBOUNCE_MS);
  }

  clearAllFilters(): void {
    this.filterUserIds = [];
    this.rangeApplied = false;
    this.dateFrom = null;
    this.dateTo = todayLocalCsDate();
    this.searchQuery = '';
    this.resetTablePagination();
    this.loadRows();
  }

  setDisplayMode(mode: MaterialDisplayMode): void {
    if (this.displayMode === mode) return;
    this.displayMode = mode;
    this.expandedSummaryCategoryIds.clear();
    this.resetTablePagination();
    this.persistDisplayModeToUrl();
  }

  private persistDisplayModeToUrl(): void {
    const p: Params = {};
    setMergeQueryDisplayMode(p, MATERIALS_DISPLAY_MODE_QUERY_KEY, this.displayMode, 'detail');
    mergeListQueryParams(this.router, this.route, p);
  }

  onMaterialPageChange(page: number): void {
    if (this.showAllRows) return;
    this.currentPage = page;
  }

  onMaterialShowAllRows(): void {
    if (this.displayLines.length <= this.itemsPerPage || this.showAllRows) return;
    this.showAllRows = true;
  }

  onMaterialExitShowAllRows(): void {
    if (!this.showAllRows) return;
    this.showAllRows = false;
    this.currentPage = 1;
  }

  private resetTablePagination(): void {
    this.showAllRows = false;
    this.currentPage = 1;
  }

  refresh(): void {
    this.loadRows();
  }

  getRowsForExport(): { detail: MaterialPanelRow[]; aggregated: MaterialAggExportRow[] } {
    return {
      detail: this.detailRows,
      aggregated: buildMaterialsExportAggregated(this.detailRows),
    };
  }

  loadRows(): void {
    const params = this.buildFetchParams();
    if (!params) return;

    this.isLoading = true;
    this.errorMessage = null;
    const planParams = this.buildPlanFetchParams(params);
    forkJoin({
      items: fetchAllMaterialsPages(this.objectService, params),
      plan: fetchMaterialsPlanBySap(this.objectService, planParams),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ items, plan }) => {
          this.detailRows = items;
          this.hasMaterialPlan = plan.hasPpd;
          this.materialPlanBySap = plan.planBySap;
          if (this.isPolygonScope) {
            this.userFilterOptionValues = mergeFacetFilterValues(
              this.userFilterOptionValues,
              this.detailRows.map((r) => r.changedBy),
              this.filterUserIds,
            );
            const allowedUsers = new Set(this.userFilterOptions.map((o) => o.value));
            this.filterUserIds = this.filterUserIds.filter((u) => allowedUsers.has(u));
          }
          this.isLoading = false;
          this.currentPage = clampPage(this.currentPage, this.displayLines.length, this.itemsPerPage);
        },
        error: (err) => {
          this.detailRows = [];
          this.hasMaterialPlan = false;
          this.materialPlanBySap = new Map();
          this.isLoading = false;
          this.errorMessage =
            err?.error?.message ??
            err?.message ??
            (this.isUserScope
              ? 'Nepodařilo se načíst materiál uživatele'
              : 'Nepodařilo se načíst materiál v polygonu');
        },
      });
  }

  private buildFetchParams(): MaterialsFetchParams | null {
    if (this.scope === 'polygon') {
      if (!this.polygonId) return null;
      return this.buildDateParams({
        polygonId: this.polygonId,
        changedBy: joinMultiFilterCsv(this.filterUserIds) || undefined,
      });
    }
    if (this.scope === 'user') {
      if (!this.userId) return null;
      return this.buildDateParams({
        changedBy: this.userId,
        polygonId: joinMultiFilterCsv(this.filterPolygonIds) || undefined,
        objectType: joinMultiFilterCsv(expandObjectTypeFilterForApi(this.filterObjectTypes)) || undefined,
        objectStatus: joinMultiFilterCsv(this.apiObjectPhases()) || undefined,
        ...(shouldSendActivePolygonOnly(this.showActiveOnly, this.filterPolygonIds.length > 0)
          ? { activePolygonOnly: true }
          : {}),
      });
    }
    return null;
  }

  private apiObjectPhases(): string[] {
    return this.filterObjectPhases.filter((p) => p !== '__MANUAL__');
  }

  private buildDateParams(base: MaterialsFetchParams): MaterialsFetchParams {
    const params: MaterialsFetchParams = { ...base };
    if (isOpenEndedDateFilter(this.dateFrom, this.dateTo)) {
      const all = unboundedDateRangeIso();
      params.dateFrom = all.from;
      params.dateTo = all.to;
    } else {
      const range = resolveDateRangeFilterIso(this.dateFrom, this.dateTo);
      params.dateFrom = range.from;
      params.dateTo = range.to;
    }
    return params;
  }

  private buildPlanFetchParams(materialsParams: MaterialsFetchParams): {
    polygonId?: string;
    dateFrom?: string;
    dateTo?: string;
    activePolygonOnly?: boolean;
  } {
    const polygonId = resolveMaterialsPlanPolygonId(
      this.scope === 'polygon' ? this.polygonId : null,
      this.scope === 'user' ? this.filterPolygonIds : undefined,
    );
    return {
      polygonId,
      dateFrom: materialsParams.dateFrom,
      dateTo: materialsParams.dateTo,
      ...(materialsParams.activePolygonOnly ? { activePolygonOnly: true } : {}),
    };
  }

  formatSummaryVariance(row: MaterialsSummaryTableRow): string {
    if (row.variance == null) return '—';
    const unit = row.plannedUnit ?? row.unit ?? 'ks';
    return this.formatQuantity(row.variance, unit);
  }

  summaryVarianceClass(row: MaterialsSummaryTableRow): string {
    if (row.variance == null) return '';
    if (row.rowType === 'total') return 'text-neutral-900 dark:text-neutral-100';
    if (row.variance >= 0) return 'text-emerald-700 dark:text-emerald-300';
    return 'text-red-700 dark:text-red-300';
  }

  formatDateTime = formatMaterialDateTime;
  formatQuantity = formatMaterialQuantity;
  formatPrice = formatMaterialPrice;

  formatSummaryQuantity(row: MaterialsSummaryTableRow): string {
    if (row.rowType === 'total') return '';
    if (row.quantity == null) return '—';
    return this.formatQuantity(row.quantity, row.unit ?? null);
  }

  formatSummaryPlannedQuantity(row: MaterialsSummaryTableRow): string {
    if (row.rowType === 'total') return '';
    if (row.plannedQuantity == null) return '—';
    return this.formatQuantity(row.plannedQuantity, row.plannedUnit ?? null);
  }

  objectTypeLabel(type: string): string {
    return getObjectTypeName(type) || type || '—';
  }

  objectDetailLinkQueryParams(): Record<string, string> {
    if (this.isUserScope) {
      const qp: Record<string, string> = { from: 'users' };
      const back = (this.objectLinkReturnUrl ?? '').trim();
      if (back && isSafeAppReturnUrl(back)) {
        qp['returnUrl'] = back;
      }
      return qp;
    }
    return { from: 'materials' };
  }

  isSummaryRowExpanded(row: MaterialsSummaryTableRow): boolean {
    return row.rowType === 'category' && this.expandedSummaryCategoryIds.has(row.id);
  }

  summaryLabelIndentClass(row: MaterialsSummaryTableRow): string {
    return categoryTreeIndentClass(row.depth ?? 0);
  }

  summaryRowClass(row: MaterialsSummaryTableRow): string {
    if (row.rowType === 'total') return categoryTreeTotalRowClass();
    if (row.rowType === 'category') return categoryTreeCategoryRowClass();
    return categoryTreeLeafRowClass();
  }

  onSummaryRowClick(row: MaterialsSummaryTableRow): void {
    if (row.rowType !== 'category') return;
    if (this.expandedSummaryCategoryIds.has(row.id)) {
      this.expandedSummaryCategoryIds.delete(row.id);
    } else {
      this.expandedSummaryCategoryIds.add(row.id);
    }
  }

  trackDisplayLine(_index: number, row: MaterialDisplayLine): string {
    return row.sourceRows[0]?.usageId ?? String(_index);
  }

  trackSummaryRow(_index: number, row: MaterialsSummaryTableRow): string {
    return row.id;
  }

  openEditRow(row: MaterialPanelRow): void {
    this.editingRow = row;
    this.editQtyStr = String(row.quantity);
    this.editError = null;
  }

  closeEditRow(): void {
    this.editingRow = null;
    this.editSaving = false;
    this.editError = null;
  }

  private parseQtyStr(value: string): number | null {
    const s = value.trim().replace(',', '.');
    if (s === '') return null;
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  saveEditRow(): void {
    if (!this.editingRow) return;
    const qty = this.parseQtyStr(this.editQtyStr);
    if (qty === null) {
      this.editError = 'Zadejte kladné množství.';
      return;
    }
    this.editSaving = true;
    this.editError = null;
    this.objectService
      .updateMaterialUsage(this.editingRow.usageId, { quantity: qty })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.closeEditRow();
          this.loadRows();
        },
        error: (err: { error?: { message?: string }; message?: string }) => {
          this.editError = err?.error?.message ?? err?.message ?? 'Nepodařilo se uložit';
          this.editSaving = false;
        },
      });
  }

  /** Smazat smí jen autor spotřeby (`changedBy` = `material_usage.user_id`). Admin to neobchází. */
  canDeleteMaterialRow(row: MaterialPanelRow | null | undefined): boolean {
    const me = String(this.authService.getCurrentUser()?.email ?? '').trim().toLowerCase();
    const owner = String(row?.changedBy ?? '').trim().toLowerCase();
    return !!me && !!owner && me === owner;
  }

  deleteRow(row: MaterialPanelRow): void {
    if (!this.canDeleteMaterialRow(row)) return;
    const label = row.name || row.sapCode || 'Tento záznam';
    if (!confirm(`Odstranit materiál „${label}" (${this.formatQuantity(row.quantity, row.unit)})?`)) return;
    this.objectService
      .deleteMaterialUsage(row.usageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadRows(),
        error: (err: { error?: { message?: string }; message?: string }) => {
          this.errorMessage = err?.error?.message ?? err?.message ?? 'Nepodařilo se odstranit';
        },
      });
  }
}
